import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Input,
  Select,
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Space,
  Modal,
  Tooltip,
  DatePicker,
} from "antd";
import {
  PlusOutlined,
  FilterOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  AlertOutlined,
  InboxOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import {
  getStockItemsStart,
  deleteStockItemStart,
  getStockStatsStart,
  getStockAlertsStart,
  clearError,
} from "../store/slices/stockSlice";
import StockForm from "../components/StockForm";
import StockDetailsModal from "../components/StockDetailsModal";
import PageHeader from "../components/PageHeader";
import type { StockItem } from "../types/Stock";
import { toast } from "../utils/toast";
import dayjs from "dayjs";

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const StockManagement: React.FC = () => {
  const dispatch = useDispatch();
  const { stockItems, loading, pagination, stats, alerts } = useSelector(
    (state: RootState) => state.stock
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isDetailsVisible, setIsDetailsVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<StockItem | null>(null);
  const [selectedItem, setSelectedItem] = useState<StockItem | null>(null);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    null
  );
  const [searchDebounceTimer, setSearchDebounceTimer] =
    useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    console.log("StockManagement component mounted, loading initial data...");

    const loadInitialData = async () => {
      try {
        // Stagger the API calls to avoid rate limiting
        const params: any = {
          page: 1,
          limit: 10,
        };

        console.log("Dispatching getStockItemsStart with params:", params);
        // First, load stock items
        dispatch(getStockItemsStart(params) as any);

        // Wait a bit then load stats
        setTimeout(() => {
          console.log("Dispatching getStockStatsStart...");
          dispatch(getStockStatsStart());
        }, 300);

        // Wait a bit more then load alerts
        setTimeout(() => {
          console.log("Dispatching getStockAlertsStart...");
          dispatch(getStockAlertsStart());
        }, 600);
      } catch (error) {
        console.error("Error loading initial data:", error);
      }
    };

    loadInitialData();

    return () => {
      dispatch(clearError());
      // Clear any pending search timer
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const fetchStockItems = (page = 1) => {
    const params: any = {
      page,
      limit: 10,
    };

    if (searchTerm) {
      params.search = searchTerm;
    }
    if (categoryFilter) {
      params.category = categoryFilter;
    }
    if (statusFilter) {
      params.status = statusFilter;
    }
    if (dateRange) {
      params.dateFrom = dateRange[0].toISOString();
      params.dateTo = dateRange[1].toISOString();
    }

    dispatch(getStockItemsStart(params) as any);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);

    // Clear existing timer
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    // Set new timer for debounced search
    const timer = setTimeout(() => {
      fetchStockItems(1);
    }, 500); // Wait 500ms after user stops typing

    setSearchDebounceTimer(timer);
  };

  const handleEdit = (item: StockItem) => {
    setEditingItem(item);
    setIsFormVisible(true);
  };

  const handleDelete = (id: string) => {
    Modal.confirm({
      title: "Delete Stock Item",
      icon: <ExclamationCircleOutlined />,
      content: "Are you sure you want to delete this stock item?",
      onOk: () => {
        dispatch(deleteStockItemStart(id) as any);
      },
    });
  };

  const handleViewDetails = (item: StockItem) => {
    setSelectedItem(item);
    setIsDetailsVisible(true);
  };

  const exportData = () => {
    if (stockItems.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      const dataToExport = stockItems.map((item) => ({
        MedicineID: item.medicineId,
        Name: item.name,
        Category: item.category,
        Manufacturer: item.manufacturer,
        BatchNumber: item.batchNumber,
        ExpiryDate: dayjs(item.expiryDate).format("DD/MM/YYYY"),
        CurrentStock: item.quantity.current,
        MinimumStock: item.quantity.minimum,
        Unit: item.quantity.unit,
        PurchasePrice: item.pricing.purchasePrice,
        SellingPrice: item.pricing.sellingPrice,
        MRP: item.pricing.mrp,
        SupplierTax: item.pricing.tax ?? 0,
        SupplierName: item.supplier?.name || "N/A",
      }));

      const csvHeader = Object.keys(dataToExport[0]).join(",");
      const csvContent = [
        csvHeader,
        ...dataToExport.map((row) =>
          Object.values(row)
            .map((value) =>
              typeof value === "string" && value.includes(",")
                ? `"${value}"`
                : value
            )
            .join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `stock-report-${dayjs().format("YYYY-MM-DD-HHmm")}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Stock data exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    }
  };

  const getStockStatus = (item: StockItem) => {
    if (item.quantity.current === 0) {
      return <Tag color="red">Out of Stock</Tag>;
    } else if (item.quantity.current <= item.quantity.minimum) {
      return <Tag color="orange">Low Stock</Tag>;
    } else {
      return <Tag color="green">In Stock</Tag>;
    }
  };

  const getExpiryStatus = (expiryDate: string) => {
    const today = dayjs();
    const expiry = dayjs(expiryDate);
    const daysToExpiry = expiry.diff(today, "days");

    if (daysToExpiry < 0) {
      return <Tag color="red">Expired</Tag>;
    } else if (daysToExpiry <= 30) {
      return <Tag color="orange">Expires Soon</Tag>;
    } else {
      return <Tag color="green">Valid</Tag>;
    }
  };

  const columns = [
    {
      title: "Item Name",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: StockItem) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            ID: {record.medicineId}
          </div>
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (category: string) => <Tag>{category}</Tag>,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      render: (quantity: any, record: StockItem) => (
        <div>
          <div>
            {record.quantity.current} {record.quantity.unit}
          </div>
          <div style={{ fontSize: "11px", color: "#999" }}>
            Min: {record.quantity.minimum}
          </div>
        </div>
      ),
    },
    {
      title: "Price",
      key: "price",
      render: (record: StockItem) => (
        <div>
          <div>Cost: ₹{record.pricing.purchasePrice}</div>
          <div>Sale: ₹{record.pricing.sellingPrice}</div>
        </div>
      ),
    },
    {
      title: "Expiry Date",
      dataIndex: "expiryDate",
      key: "expiryDate",
      render: (date: string) => (
        <div>
          <div>{dayjs(date).format("DD/MM/YYYY")}</div>
          {getExpiryStatus(date)}
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (record: StockItem) => getStockStatus(record),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: StockItem) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record._id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Stats Cards */}
      <Row gutter={8} style={{ marginBottom: 10 }}>
        <Col span={6}>
          <Card className="stat-card" size="small">
            <Statistic
              title="Total Items"
              value={stats?.totalItems || 0}
              prefix={<InboxOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card" size="small">
            <Statistic
              title="Low Stock Items"
              value={stats?.lowStockItems || 0}
              valueStyle={{ color: "#faad14" }}
              prefix={<AlertOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card" size="small">
            <Statistic
              title="Out of Stock"
              value={stats?.expiredItems || 0}
              valueStyle={{ color: "#f5222d" }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card" size="small">
            <Statistic
              title="Total Value"
              value={stats?.stockValue?.totalSellingValue || 0}
              precision={2}
              prefix="₹"
            />
          </Card>
        </Col>
      </Row>

      {/* Alerts */}
      {alerts &&
        alerts.summary &&
        (alerts.summary.lowStockCount > 0 ||
          alerts.summary.expiringCount > 0 ||
          alerts.summary.expiredCount > 0) && (
          <Card
            title={
              <span>
                <AlertOutlined
                  style={{ color: "#faad14", marginRight: "8px" }}
                />
                Stock Alerts (
                {alerts.summary.lowStockCount +
                  alerts.summary.expiringCount +
                  alerts.summary.expiredCount}
                )
              </span>
            }
            style={{ marginBottom: 10 }}
            size="small"
          >
            {alerts.lowStock.slice(0, 2).map((item, index) => (
              <div key={`low-${index}`} style={{ marginBottom: "8px" }}>
                <Tag color="orange">Low Stock</Tag>
                <span>
                  {item.name} - Only {item.quantity.current}{" "}
                  {item.quantity.unit} left
                </span>
              </div>
            ))}
            {alerts.expiring.slice(0, 2).map((item, index) => (
              <div key={`expiring-${index}`} style={{ marginBottom: "8px" }}>
                <Tag color="yellow">Expiring Soon</Tag>
                <span>
                  {item.name} - Expires on{" "}
                  {dayjs(item.expiryDate).format("DD/MM/YYYY")}
                </span>
              </div>
            ))}
            {alerts.expired.slice(0, 2).map((item, index) => (
              <div key={`expired-${index}`} style={{ marginBottom: "8px" }}>
                <Tag color="red">Expired</Tag>
                <span>
                  {item.name} - Expired on{" "}
                  {dayjs(item.expiryDate).format("DD/MM/YYYY")}
                </span>
              </div>
            ))}
            {alerts.summary.lowStockCount +
              alerts.summary.expiringCount +
              alerts.summary.expiredCount >
              6 && (
              <div style={{ color: "#666", fontSize: "12px" }}>
                +
                {alerts.summary.lowStockCount +
                  alerts.summary.expiringCount +
                  alerts.summary.expiredCount -
                  6}{" "}
                more alerts
              </div>
            )}
          </Card>
        )}

      <PageHeader
        title="Stock Management"
        extra={
          <>
            <Button
              icon={<FilterOutlined />}
              onClick={() => fetchStockItems(1)}
            >
              Filter
            </Button>
            <Button
              onClick={() => exportData()}
              icon={<DownloadOutlined />}
              title="Export Stock Data"
            >
              Export CSV
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingItem(null);
                setIsFormVisible(true);
              }}
            >
              Add Opening Stock
            </Button>
            <Button
              type="primary"
              onClick={() => (window.location.href = "/purchases/new")}
            >
              New Purchase Invoice
            </Button>
          </>
        }
      />

      <div className="page-panel">
        {/* Filters */}
        <Row gutter={8} style={{ marginBottom: 10 }}>
          <Col span={8}>
            <Search
              placeholder="Search by name, SKU, or batch number"
              allowClear
              onSearch={handleSearch}
              onChange={(e) => {
                // Only update the search term, don't trigger API call immediately
                setSearchTerm(e.target.value);
                handleSearch(e.target.value);
              }}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Category"
              allowClear
              style={{ width: "100%" }}
              onChange={setCategoryFilter}
            >
              <Option value="tablet">Tablet</Option>
              <Option value="capsule">Capsule</Option>
              <Option value="syrup">Syrup</Option>
              <Option value="injection">Injection</Option>
              <Option value="cream">Cream</Option>
              <Option value="drops">Drops</Option>
              <Option value="inhaler">Inhaler</Option>
              <Option value="other">Other</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Status"
              allowClear
              style={{ width: "100%" }}
              onChange={setStatusFilter}
            >
              <Option value="in_stock">In Stock</Option>
              <Option value="low_stock">Low Stock</Option>
              <Option value="out_of_stock">Out of Stock</Option>
            </Select>
          </Col>
          <Col span={6}>
            <RangePicker
              placeholder={["From Date", "To Date"]}
              style={{ width: "100%" }}
              onChange={(dates) =>
                setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])
              }
            />
          </Col>
          <Col span={2}>
            <Button
              icon={<FilterOutlined />}
              onClick={() => fetchStockItems(1)}
            >
              Filter
            </Button>
          </Col>
        </Row>

        {/* Table */}
        <Table
          className="dense-table"
          size="small"
          columns={columns}
          dataSource={stockItems}
          rowKey="_id"
          loading={loading}
          pagination={{
            current: pagination?.currentPage || 1,
            total: pagination?.totalItems || 0,
            pageSize: 10,
            showSizeChanger: false,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            onChange: fetchStockItems,
          }}
        />
      </div>

      {/* Stock Form Modal */}
      <StockForm
        visible={isFormVisible}
        onCancel={() => {
          setIsFormVisible(false);
          setEditingItem(null);
        }}
        editData={editingItem}
        onSuccess={() => {
          setIsFormVisible(false);
          setEditingItem(null);

          // Stagger the refresh calls to avoid rate limiting
          fetchStockItems();
          setTimeout(() => {
            dispatch(getStockStatsStart());
          }, 300);
          setTimeout(() => {
            dispatch(getStockAlertsStart());
          }, 600);
        }}
      />

      {/* Stock Details Modal */}
      <StockDetailsModal
        visible={isDetailsVisible}
        onCancel={() => {
          setIsDetailsVisible(false);
          setSelectedItem(null);
        }}
        stockItem={selectedItem}
      />
    </div>
  );
};

export default StockManagement;
